import {
  clock,
  DEFAULT_HOURS,
  FACILITY_SUGGESTIONS,
  formatRupees,
  indiaNow,
  type ListingInput,
  WEEKDAYS,
} from '@quickreport/domain';
import { Badge, Button, Field, Input, Select, Tabs, Textarea } from '@quickreport/ui';
import clsx from 'clsx';
import { CheckCircle2, Copy, ExternalLink, ImagePlus, MapPin, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { describeError } from '../../lib/api';
import { bookingsApi, type ListingState } from '../../lib/bookings';
import { useWorkspace } from '../../lib/workspace';
import { ImageError, loadImage, type StoredImage, storeImage } from '../../local-db/files';

type Tab = 'details' | 'location' | 'services' | 'timings' | 'tests' | 'facilities';

const rupees = (paise: number) => (paise ? String(paise / 100) : '');
const paise = (text: string) =>
  Math.max(0, Math.round(Number(text.replace(/[^\d.]/g, '') || 0) * 100));

/**
 * The lab's page in the LabView app, where people find labs near them and book tests. Nothing is
 * public until the lab switches it on; each detail can follow Report Settings or be its own.
 */
export function PublicPage() {
  const ws = useWorkspace();
  const [state, setState] = useState<ListingState | null>(null);
  const [l, setL] = useState<ListingInput | null>(null);
  const [saved, setSaved] = useState('');
  const [tab, setTab] = useState<Tab>('details');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const s = await bookingsApi.listing();
      setState(s);
      setL(s.listing);
      setSaved(JSON.stringify(s.listing));
    } catch (err) {
      setMessage({ tone: 'error', text: describeError(err) });
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: loads once
  useEffect(() => {
    void load();
  }, []);

  if (!l || !state) {
    return (
      <div className="p-8 text-slate">
        {message ? <p className="text-flag">{message.text}</p> : 'Opening your public page…'}
      </div>
    );
  }
  const dirty = JSON.stringify(l) !== saved;
  const set = (patch: Partial<ListingInput>) => setL((x) => (x ? { ...x, ...patch } : x));

  async function save(next = l) {
    if (!next) return;
    setBusy(true);
    try {
      const r = await bookingsApi.saveListing(next);
      setSaved(JSON.stringify(next));
      setL(next);
      setState((s) => (s ? { ...s, problems: r.problems, located: r.located } : s));
      setMessage({
        tone: 'ok',
        text: next.enabled
          ? `Saved. Your lab is live in the LabView app.${next.mapsUrl && !r.located ? ' We could not read the location from that maps link, so "near me" will not find you yet.' : ''}`
          : 'Saved. Your page is hidden; switch it on when it is ready.',
      });
    } catch (err) {
      setMessage({ tone: 'error', text: describeError(err) });
    }
    setBusy(false);
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-paper px-4 py-3 md:px-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">Public page</h1>
          <p className="text-slate max-md:hidden">
            How people find your lab in the LabView app, see your tests and timings, and book a
            visit or a home collection.
          </p>
        </div>
        <Button tone="primary" onClick={() => void save()} disabled={!dirty || busy}>
          Save
        </Button>
      </header>
      {message ? (
        <div
          className={`border-b px-4 py-2 text-[13px] md:px-6 ${message.tone === 'ok' ? 'border-green-200 bg-green-50 text-ok' : 'border-red-200 bg-red-50 text-flag'}`}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
        <div className="grid max-w-3xl gap-5">
          <section
            className={clsx(
              'flex flex-wrap items-center gap-4 rounded-xl border p-4',
              l.enabled ? 'border-green-300 bg-green-50/60' : 'border-line bg-paper',
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold">Show my lab in the LabView app</h2>
                {l.enabled && saved.includes('"enabled":true') ? (
                  <Badge tone="ok">Live</Badge>
                ) : (
                  <Badge>Hidden</Badge>
                )}
              </div>
              <p className="text-[13px] text-slate">
                People nearby can find you, see your tests and prices, and send booking requests for
                you to approve.
              </p>
              {state.problems.length && !l.enabled ? (
                <p className="mt-1 text-[13px] text-warn">
                  Still to do: {state.problems.join(' · ')}
                </p>
              ) : null}
            </div>
            <Toggle
              checked={l.enabled}
              label="Show my lab publicly"
              onChange={(on) => {
                set({ enabled: on });
                if (on) setMessage(null);
              }}
            />
          </section>

          <Tabs
            label="Public page sections"
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'details', label: 'Lab details' },
              { value: 'location', label: 'Location' },
              { value: 'services', label: 'Services & booking' },
              { value: 'timings', label: 'Timings' },
              { value: 'tests', label: 'Tests' },
              { value: 'facilities', label: 'Facilities' },
            ]}
          />
          <div className="grid gap-5">
            {tab === 'details' ? (
              <Details
                l={l}
                set={set}
                fromReport={state.fromReport}
                db={ws.db}
                orgId={ws.orgId}
                onError={(t) => setMessage({ tone: 'error', text: t })}
              />
            ) : null}
            {tab === 'location' ? <Location l={l} set={set} located={state.located} /> : null}
            {tab === 'services' ? <Services l={l} set={set} /> : null}
            {tab === 'timings' ? <Timings l={l} set={set} /> : null}
            {tab === 'tests' ? <Tests l={l} set={set} tests={state.tests} /> : null}
            {tab === 'facilities' ? <Facilities l={l} set={set} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative h-7 w-12 shrink-0 rounded-full transition-colors',
        checked ? 'bg-ok' : 'bg-line',
      )}
    >
      <span
        className={clsx(
          'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all',
          checked ? 'left-[1.375rem]' : 'left-0.5',
        )}
      />
    </button>
  );
}

function Check({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-brand"
      />
      <span>{children}</span>
    </label>
  );
}

type Set = (p: Partial<ListingInput>) => void;

// ---- details ----

function Details({
  l,
  set,
  fromReport,
  db,
  orgId,
  onError,
}: {
  l: ListingInput;
  set: Set;
  fromReport: ListingState['fromReport'];
  db: ReturnType<typeof useWorkspace>['db'];
  orgId: string;
  onError: (t: string) => void;
}) {
  const same = l.sameAsReport;
  const setSame = (k: keyof ListingInput['sameAsReport'], on: boolean) =>
    set({
      sameAsReport: { ...same, [k]: on },
      // Turning "same" off starts from the report's value, ready to change.
      ...(on
        ? {}
        : k === 'logo'
          ? { logoFileId: l.logoFileId ?? fromReport.logoFileId }
          : { [k]: (l[k] as string) || fromReport[k] }),
    });
  const [logo, setLogo] = useState<StoredImage | null>(null);
  const logoId = same.logo ? fromReport.logoFileId : l.logoFileId;
  useEffect(() => {
    let alive = true;
    loadImage(db, logoId).then((i) => alive && setLogo(i));
    return () => {
      alive = false;
    };
  }, [db, logoId]);
  const logoUrl = useMemo(
    () =>
      logo ? URL.createObjectURL(new Blob([logo.bytes as BlobPart], { type: logo.mime })) : null,
    [logo],
  );
  const input = useRef<HTMLInputElement>(null);

  const row = (k: 'name' | 'address' | 'phone' | 'email', label: string, multiline = false) => (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] font-medium text-slate">{label}</span>
        <Check checked={same[k]} onChange={(on) => setSame(k, on)}>
          <span className="text-[12px] text-slate">Same as Report Settings</span>
        </Check>
      </div>
      {same[k] ? (
        <div className="min-h-8 rounded-lg border border-dashed border-line bg-canvas px-2.5 py-1.5 text-[13px] whitespace-pre-line text-slate">
          {fromReport[k] || <em>Not set in Report Settings</em>}
        </div>
      ) : multiline ? (
        <Textarea rows={2} value={l[k]} onChange={(e) => set({ [k]: e.target.value })} />
      ) : (
        <Input value={l[k]} onChange={(e) => set({ [k]: e.target.value })} />
      )}
    </div>
  );

  return (
    <>
      <p className="text-[13px] text-slate">
        What you print on reports and what you show publicly can differ. Untick “Same as Report
        Settings” to use something else here.
      </p>
      {row('name', 'Lab name')}
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-medium text-slate">Logo</span>
          <Check checked={same.logo} onChange={(on) => setSame('logo', on)}>
            <span className="text-[12px] text-slate">Same as Report Settings</span>
          </Check>
        </div>
        <div className="flex items-center gap-4">
          <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-xl border border-line bg-white">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-[11px] text-slate">No logo</span>
            )}
          </div>
          {same.logo ? null : (
            <>
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    set({ logoFileId: await storeImage(db, orgId, f, { maxSide: 600 }) });
                  } catch (err) {
                    onError(
                      err instanceof ImageError ? err.message : 'Could not use that picture.',
                    );
                  }
                }}
              />
              <Button onClick={() => input.current?.click()}>
                <ImagePlus size={15} /> {logoId ? 'Change' : 'Upload'}
              </Button>
            </>
          )}
        </div>
        <span className="text-[12px] text-slate">
          A square logo looks best in the list of labs.
        </span>
      </div>
      {row('address', 'Address', true)}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Area / locality" hint="e.g. Lal Chowk">
          <Input value={l.area} onChange={(e) => set({ area: e.target.value })} />
        </Field>
        <Field label="City">
          <Input value={l.city} onChange={(e) => set({ city: e.target.value })} />
        </Field>
        <Field label="PIN code">
          <Input
            inputMode="numeric"
            maxLength={6}
            value={l.pincode}
            onChange={(e) => set({ pincode: e.target.value.replace(/\D/g, '') })}
          />
        </Field>
      </div>
      {row('phone', 'Phone numbers')}
      <Field
        label="WhatsApp number"
        hint="People can message you with one tap. Leave empty to hide the button."
      >
        <Input
          inputMode="tel"
          value={l.whatsapp}
          onChange={(e) => set({ whatsapp: e.target.value })}
          placeholder="98765 43210"
        />
      </Field>
      {row('email', 'Email')}
      <Field
        label="About your lab"
        hint="A few lines: since when, what you are known for, your doctors."
      >
        <Textarea
          rows={4}
          maxLength={1500}
          value={l.about}
          onChange={(e) => set({ about: e.target.value })}
        />
      </Field>
    </>
  );
}

// ---- location ----

function Location({ l, set, located }: { l: ListingInput; set: Set; located: boolean }) {
  return (
    <>
      <Field
        label="Google Maps link"
        hint="Open Google Maps, find your lab, tap Share and Copy link, then paste it here. People tap it for directions, and it lets “near me” find you."
      >
        <Input
          value={l.mapsUrl}
          onChange={(e) => set({ mapsUrl: e.target.value.trim() })}
          placeholder="https://maps.app.goo.gl/…"
        />
      </Field>
      {l.mapsUrl ? (
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          {located ? (
            <span className="flex items-center gap-1 text-ok">
              <CheckCircle2 size={15} /> Location found: “near me” finds your lab
            </span>
          ) : (
            <span className="flex items-center gap-1 text-slate">
              <MapPin size={15} /> The location is read when you save
            </span>
          )}
          <a
            href={l.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-navy hover:underline"
          >
            Open the link <ExternalLink size={13} />
          </a>
        </div>
      ) : null}
    </>
  );
}

// ---- services ----

function Services({ l, set }: { l: ListingInput; set: Set }) {
  return (
    <>
      <Card>
        <Check checked={l.acceptsBookings} onChange={(v) => set({ acceptsBookings: v })}>
          <span className="block font-medium">Take bookings in the app</span>
          <span className="block text-[13px] text-slate">
            People choose tests, a day and a time; you approve or decline each request.
          </span>
        </Check>
        {l.acceptsBookings ? (
          <div className="mt-3 grid gap-4 pl-6 sm:grid-cols-2">
            <Field label="Length of a slot" hint="People choose a time on this step.">
              <Select
                value={l.slotMinutes}
                onChange={(e) => set({ slotMinutes: Number(e.target.value) })}
              >
                {[15, 20, 30, 45, 60, 90, 120].map((m) => (
                  <option key={m} value={m}>
                    {m < 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? '' : 's'}`}
                  </option>
                ))}
              </Select>
            </Field>
            <Capacity
              label="People at the lab at one time"
              hint="Several people can book the same time; when the slot is full it closes."
              value={l.slotCapacity}
              onChange={(n) => set({ slotCapacity: n })}
            />
            {l.homeCollection ? (
              <Capacity
                label="Home collections at one time"
                hint="How many homes your collectors can visit in one slot."
                value={l.homeSlotCapacity}
                onChange={(n) => set({ homeSlotCapacity: n })}
              />
            ) : null}
          </div>
        ) : null}
      </Card>
      <Card>
        <Check checked={l.homeCollection} onChange={(v) => set({ homeCollection: v })}>
          <span className="block font-medium">Sample collection at home</span>
          <span className="block text-[13px] text-slate">
            Your collector visits the patient. People enter their address when booking.
          </span>
        </Check>
        {l.homeCollection ? (
          <div className="mt-3 grid gap-4 pl-6 sm:grid-cols-2">
            <Field label="Home collection fee (₹)" hint="0 for free collection.">
              <Input
                inputMode="decimal"
                value={rupees(l.homeCollectionFeePaise)}
                onChange={(e) => set({ homeCollectionFeePaise: paise(e.target.value) })}
                placeholder="0"
              />
            </Field>
            <Field label="Up to how far (km)" hint="Shown to people; 0 to not say.">
              <Input
                type="number"
                min={0}
                max={200}
                value={l.homeCollectionKm}
                onChange={(e) =>
                  set({ homeCollectionKm: Math.max(0, Math.min(200, Number(e.target.value) || 0)) })
                }
              />
            </Field>
          </div>
        ) : null}
      </Card>
      <Card>
        <Check checked={l.reportDelivery} onChange={(v) => set({ reportDelivery: v })}>
          <span className="block font-medium">Deliver printed reports at home</span>
          <span className="block text-[13px] text-slate">
            People can ask for it when booking. Reports also reach them on WhatsApp as always.
          </span>
        </Check>
        {l.reportDelivery ? (
          <div className="mt-3 grid gap-4 pl-6 sm:grid-cols-2">
            <Field label="Delivery fee (₹)">
              <Input
                inputMode="decimal"
                value={rupees(l.reportDeliveryFeePaise)}
                onChange={(e) => set({ reportDeliveryFeePaise: paise(e.target.value) })}
                placeholder="0"
              />
            </Field>
          </div>
        ) : null}
      </Card>
    </>
  );
}

/** A number of people, or no limit (stored as 0). */
function Capacity({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (n: number) => void;
}) {
  const unlimited = value === 0;
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        <Input
          type="number"
          min={1}
          max={500}
          className="w-24"
          disabled={unlimited}
          value={unlimited ? '' : value}
          placeholder="—"
          onChange={(e) => onChange(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
        />
        <label className="flex items-center gap-1.5 text-[13px]">
          <input
            type="checkbox"
            className="h-4 w-4 accent-brand"
            checked={unlimited}
            onChange={(e) => onChange(e.target.checked ? 0 : 5)}
          />
          No limit
        </label>
      </div>
    </Field>
  );
}

const Card = ({ children }: { children: React.ReactNode }) => (
  <section className="rounded-xl border border-line bg-paper p-4">{children}</section>
);

// ---- timings ----

function Timings({ l, set }: { l: ListingInput; set: Set }) {
  const hours = l.hours.length === 7 ? l.hours : DEFAULT_HOURS;
  const setDay = (i: number, patch: Partial<(typeof hours)[number]>) =>
    set({ hours: hours.map((h, j) => (j === i ? { ...h, ...patch } : h)) });
  const [holiday, setHoliday] = useState('');
  const today = indiaNow().date;
  return (
    <>
      <Card>
        <Check checked={l.open24h} onChange={(v) => set({ open24h: v })}>
          <span className="block font-medium">Open 24 hours, every day</span>
        </Check>
      </Card>
      {l.open24h ? null : (
        <section className="rounded-xl border border-line bg-paper">
          {hours.map((h, i) => (
            <div
              key={WEEKDAYS[i]}
              className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2.5 last:border-0"
            >
              <label className="flex w-28 items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand"
                  checked={h.open}
                  onChange={(e) => setDay(i, { open: e.target.checked })}
                />
                {WEEKDAYS[i]}
              </label>
              {h.open ? (
                <>
                  <Input
                    type="time"
                    aria-label={`${WEEKDAYS[i]} opens`}
                    className="w-32"
                    value={h.from}
                    onChange={(e) => setDay(i, { from: e.target.value })}
                  />
                  <span className="text-slate">to</span>
                  <Input
                    type="time"
                    aria-label={`${WEEKDAYS[i]} closes`}
                    className="w-32"
                    value={h.to}
                    onChange={(e) => setDay(i, { to: e.target.value })}
                  />
                  <span className="text-[12px] text-slate max-sm:hidden">
                    {clock(h.from)} – {clock(h.to)}
                  </span>
                  {h.from >= h.to ? (
                    <span className="text-[12px] text-flag">Closes before it opens</span>
                  ) : null}
                </>
              ) : (
                <span className="text-slate">Closed</span>
              )}
              {i === 0 ? (
                <Button
                  size="sm"
                  tone="ghost"
                  className="ml-auto"
                  onClick={() =>
                    set({
                      hours: hours.map((x, j) => (j < 6 ? { ...hours[0] } : x)) as typeof hours,
                    })
                  }
                >
                  <Copy size={13} /> Same for Mon–Sat
                </Button>
              ) : null}
            </div>
          ))}
        </section>
      )}
      <section className="grid gap-2">
        <h2 className="text-[13px] font-semibold">Holidays</h2>
        <p className="text-[12px] text-slate">
          Days you are closed although the week says open. Nobody can book them.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            min={today}
            className="w-44"
            value={holiday}
            onChange={(e) => setHoliday(e.target.value)}
          />
          <Button
            size="sm"
            disabled={!holiday}
            onClick={() => {
              set({ closedDates: [...new Set([...l.closedDates, holiday])].sort() });
              setHoliday('');
            }}
          >
            <Plus size={13} /> Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {l.closedDates
            .filter((d) => d >= today)
            .map((d) => (
              <span
                key={d}
                className="flex items-center gap-1 rounded-full bg-canvas px-2.5 py-1 text-[12px] ring-1 ring-line"
              >
                {new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  timeZone: 'UTC',
                })}
                <button
                  type="button"
                  aria-label={`Remove ${d}`}
                  onClick={() => set({ closedDates: l.closedDates.filter((x) => x !== d) })}
                  className="text-slate hover:text-flag"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
        </div>
      </section>
    </>
  );
}

// ---- tests ----

function Tests({ l, set, tests }: { l: ListingInput; set: Set; tests: ListingState['tests'] }) {
  const [q, setQ] = useState('');
  const hidden = new Set(l.hiddenTemplates);
  const noPrice = new Set(l.hiddenPrices);
  const shown = tests.filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (list: 'hiddenTemplates' | 'hiddenPrices', id: string, on: boolean) => {
    const s = new Set(l[list]);
    if (on) s.add(id);
    else s.delete(id);
    set({ [list]: [...s] });
  };
  return (
    <>
      <p className="text-[13px] text-slate">
        Every test in your templates shows on your page by itself, new ones too. Untick the ones you
        do not want people to book.
      </p>
      <Card>
        <Check checked={l.hidePrices} onChange={(v) => set({ hidePrices: v })}>
          <span className="block font-medium">Hide all prices</span>
          <span className="block text-[13px] text-slate">
            People see the tests and ask you for the price.
          </span>
        </Check>
      </Card>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-xs"
          placeholder={`Search ${tests.length} tests`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span className="text-[13px] text-slate">
          {tests.length - hidden.size} of {tests.length} shown
        </span>
        <Button
          size="sm"
          tone="ghost"
          className="ml-auto"
          onClick={() => set({ hiddenTemplates: [] })}
        >
          Show all
        </Button>
        <Button
          size="sm"
          tone="ghost"
          onClick={() => set({ hiddenTemplates: tests.map((t) => t.id) })}
        >
          Hide all
        </Button>
      </div>
      <ul className="divide-y divide-line rounded-xl border border-line bg-paper">
        {shown.length === 0 ? (
          <li className="p-4 text-center text-slate">No tests match.</li>
        ) : null}
        {shown.map((t) => (
          <li
            key={t.id}
            className={clsx(
              'flex flex-wrap items-center gap-3 px-4 py-2',
              hidden.has(t.id) && 'opacity-60',
            )}
          >
            <label className="flex min-w-0 flex-1 items-center gap-2.5">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand"
                checked={!hidden.has(t.id)}
                onChange={(e) => toggle('hiddenTemplates', t.id, !e.target.checked)}
              />
              <span className="min-w-0">
                <span className="block truncate font-medium">{t.name}</span>
                {t.department ? (
                  <span className="block text-[12px] text-slate">{t.department}</span>
                ) : null}
              </span>
            </label>
            {l.hidePrices || hidden.has(t.id) ? null : t.pricePaise ? (
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand"
                  checked={!noPrice.has(t.id)}
                  onChange={(e) => toggle('hiddenPrices', t.id, !e.target.checked)}
                />
                Show price {formatRupees(t.pricePaise)}
              </label>
            ) : (
              <span className="text-[12px] text-slate">No price set</span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

// ---- facilities ----

function Facilities({ l, set }: { l: ListingInput; set: Set }) {
  const [own, setOwn] = useState('');
  const has = new Set(l.facilities);
  const flip = (f: string) =>
    set({ facilities: has.has(f) ? l.facilities.filter((x) => x !== f) : [...l.facilities, f] });
  const custom = l.facilities.filter(
    (f) => !(FACILITY_SUGGESTIONS as readonly string[]).includes(f),
  );
  return (
    <>
      <p className="text-[13px] text-slate">
        What people can expect at your lab. These show as small tags on your page.
      </p>
      <div className="flex flex-wrap gap-2">
        {[...FACILITY_SUGGESTIONS, ...custom].map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={has.has(f)}
            onClick={() => flip(f)}
            className={clsx(
              'rounded-full px-3 py-1.5 text-[13px] ring-1 transition-colors',
              has.has(f)
                ? 'bg-brand-soft font-medium text-brand ring-brand'
                : 'bg-paper text-slate ring-line hover:ring-brand',
            )}
          >
            {has.has(f) ? '✓ ' : ''}
            {f}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          className="max-w-xs"
          placeholder="Add your own, e.g. Free Wi-Fi"
          value={own}
          maxLength={60}
          onChange={(e) => setOwn(e.target.value)}
        />
        <Button
          disabled={!own.trim()}
          onClick={() => {
            set({ facilities: [...new Set([...l.facilities, own.trim()])] });
            setOwn('');
          }}
        >
          <Plus size={15} /> Add
        </Button>
      </div>
    </>
  );
}
